import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image, Modal, Animated, useWindowDimensions } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { auth } from '../firebaseConfig';
import { getUserProfile } from '../services/profileService';
import { signOut } from 'firebase/auth';

export default function UserMenu({ 
  navigation, 
  currentScreen, 
  isMobile = false, 
  hideHamburger = false,
  externalControl = false,
  showMenu: externalShowMenu,
  onClose
}) {
  const { colors, isDark, setTheme } = useTheme();
  const { currentOrganization, organizations, switchOrganization, pendingInvitations } = useOrganization();
  const [internalShowMenu, setInternalShowMenu] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [slideAnim] = useState(new Animated.Value(-300));
  const { width } = useWindowDimensions();
  const isMobileFromWidth = width < 1024; // Changed to 1024 for tablet support
  const isMobileView = (isMobile || isMobileFromWidth) && !hideHamburger; // Use prop or auto-detect, but skip if hideHamburger
  const isAdmin = userProfile?.userType === 'admin';
  
  // Use external control if provided, otherwise use internal state
  const showMenu = externalControl ? externalShowMenu : internalShowMenu;
  const handleCloseMenu = () => {
    if (externalControl && onClose) {
      onClose();
    } else {
      setInternalShowMenu(false);
    }
  };
  const handleOpenMenu = () => {
    if (!externalControl) {
      setInternalShowMenu(true);
    }
  };
  const handleToggleMenu = () => {
    if (externalControl) {
      // Can't toggle when externally controlled
      return;
    }
    setInternalShowMenu(!internalShowMenu);
  };

  useEffect(() => {
    loadUserProfile();
  }, []);

  useEffect(() => {
    if (isMobileView && showMenu) {
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        friction: 8,
      }).start();
    } else if (isMobileView && !showMenu) {
      Animated.spring(slideAnim, {
        toValue: -300,
        useNativeDriver: true,
        friction: 8,
      }).start();
    }
  }, [showMenu, isMobileView]);

  const loadUserProfile = async () => {
    try {
      const profile = await getUserProfile(auth.currentUser.uid);
      setUserProfile(profile);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out: ' + error.message);
    }
  };

  const handleNavigate = (screen) => {
    handleCloseMenu();
    navigation.navigate(screen);
  };

  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  const profileImageUrl = userProfile?.photoURL || auth.currentUser?.photoURL;
  const displayName = userProfile?.displayName || auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'User';

  // Mobile Hamburger Menu
  if (isMobileView) {
    return (
      <>
        {/* Hamburger Button */}
        {!externalControl && (
          <TouchableOpacity 
            style={styles.hamburgerButton} 
            onPress={handleOpenMenu}
          >
            <View style={styles.hamburgerLine} />
            <View style={styles.hamburgerLine} />
            <View style={styles.hamburgerLine} />
          </TouchableOpacity>
        )}

        {/* Mobile Overlay Menu */}
        <Modal
          visible={showMenu}
          transparent
          animationType="none"
          onRequestClose={handleCloseMenu}
        >
          <View style={styles.mobileOverlay}>
            <TouchableOpacity 
              style={styles.mobileOverlayBackground} 
              onPress={handleCloseMenu}
              activeOpacity={1}
            />
            <Animated.View 
              style={[
                styles.mobileMenuPanel,
                { transform: [{ translateX: slideAnim }] }
              ]}
            >
              {/* Profile Header */}
              <View style={styles.mobileMenuHeader}>
                <View style={styles.profileImageContainer}>
                  {profileImageUrl ? (
                    <Image source={{ uri: profileImageUrl }} style={styles.profileImage} />
                  ) : (
                    <View style={styles.profileImagePlaceholder}>
                      <Text style={styles.profileImagePlaceholderText}>
                        {displayName.charAt(0).toUpperCase()}
                      </Text>
                    </View>
                  )}
                </View>
                <Text style={styles.mobileMenuName}>{displayName}</Text>
                <Text style={styles.mobileMenuEmail}>{auth.currentUser?.email}</Text>
              </View>

              {/* Organization Switcher */}
              {currentOrganization && organizations && organizations.length > 0 && (
                <View style={[styles.orgSwitcher, { backgroundColor: colors.secondary, borderColor: colors.border }]}>
                  <Text style={[styles.orgLabel, { color: colors.textSecondary }]}>Organization</Text>
                  <View style={styles.orgSelector}>
                    {organizations.map((org) => (
                      <TouchableOpacity
                        key={org.id}
                        style={[
                          styles.orgOption,
                          { backgroundColor: colors.card, borderColor: colors.border },
                          currentOrganization.id === org.id && { borderColor: colors.accent, borderWidth: 2 }
                        ]}
                        onPress={() => switchOrganization(org.id)}
                      >
                        <Text style={[styles.orgName, { color: currentOrganization.id === org.id ? colors.accent : colors.text }]}>
                          {org.name}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                  <TouchableOpacity
                    style={[styles.manageOrgButton, { backgroundColor: colors.accent }]}
                    onPress={() => handleNavigate('organization')}
                  >
                    <Text style={[styles.manageOrgText, { color: '#fff' }]}>Manage Organization</Text>
                  </TouchableOpacity>
                  {pendingInvitations && pendingInvitations.length > 0 && (
                    <View style={[styles.inviteBadge, { backgroundColor: colors.accent }]}>
                      <Text style={[styles.inviteBadgeText, { color: '#fff' }]}>{pendingInvitations.length} invite(s)</Text>
                    </View>
                  )}
                </View>
              )}

              {/* Menu Items */}
              <View style={styles.mobileMenuItems}>
                <TouchableOpacity 
                  style={[styles.mobileMenuItem, styles.themeToggleMenuItem]}
                  onPress={toggleTheme}
                >
                  <Text style={styles.mobileMenuItemText}>
                    {isDark ? '☀️ Light Mode' : '🌙 Dark Mode'}
                  </Text>
                </TouchableOpacity>

                <View style={[styles.menuDivider, { backgroundColor: colors.border }]} />

                <TouchableOpacity 
                  style={styles.mobileMenuItem}
                  onPress={() => handleNavigate('dashboard')}
                >
                  <Text style={styles.mobileMenuItemText}>Dashboard</Text>
                </TouchableOpacity>

                <TouchableOpacity 
                  style={styles.mobileMenuItem}
                  onPress={() => handleNavigate('profile')}
                >
                  <Text style={styles.mobileMenuItemText}>My Profile</Text>
                </TouchableOpacity>

                <TouchableOpacity 
                  style={styles.mobileMenuItem}
                  onPress={() => handleNavigate('wizard')}
                >
                  <Text style={styles.mobileMenuItemText}>Generate Proposal</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.mobileMenuItem}
                  onPress={() => handleNavigate('contracts')}
                >
                  <Text style={styles.mobileMenuItemText}>📝 Contract Generator</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.mobileMenuItem}
                  onPress={() => handleNavigate('tasks')}
                >
                  <Text style={styles.mobileMenuItemText}>Task Manager</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.mobileMenuItem}
                  onPress={() => handleNavigate('casestudy')}
                >
                  <Text style={styles.mobileMenuItemText}>Case Study Creator</Text>
                </TouchableOpacity>

                <TouchableOpacity
                   style={styles.mobileMenuItem}
                   onPress={() => handleNavigate('settings')}
                 >
                   <Text style={styles.mobileMenuItemText}>Templates & Examples</Text>
                 </TouchableOpacity>

                {isAdmin && (
                  <TouchableOpacity 
                    style={styles.mobileMenuItem}
                    onPress={() => handleNavigate('admin')}
                  >
                    <Text style={styles.mobileMenuItemText}>Admin Dashboard</Text>
                  </TouchableOpacity>
                )}

                <View style={styles.menuDivider} />

                <TouchableOpacity 
                  style={styles.mobileMenuItem}
                  onPress={handleSignOut}
                >
                  <Text style={[styles.mobileMenuItemText, styles.signOutText]}>Sign Out</Text>
                </TouchableOpacity>
              </View>

              {/* Close Button */}
              <TouchableOpacity 
                style={styles.closeButton}
                onPress={handleCloseMenu}
              >
                <Text style={styles.closeButtonText}>✕</Text>
              </TouchableOpacity>
            </Animated.View>
          </View>
        </Modal>
      </>
    );
  }

  // Desktop Dropdown Menu
  return (
    <>
      {showMenu && (
        <TouchableOpacity 
          style={styles.desktopOverlay}
          onPress={handleCloseMenu}
          activeOpacity={1}
        />
      )}
      
      <View style={styles.desktopMenuContainer}>
        <TouchableOpacity 
          style={styles.profileButton}
          onPress={handleToggleMenu}
          className="profile-button"
        >
          {profileImageUrl ? (
            <Image source={{ uri: profileImageUrl }} style={styles.profileImageSmall} />
          ) : (
            <View style={styles.profileImagePlaceholderSmall}>
              <Text style={styles.profileImagePlaceholderTextSmall}>
                {displayName.charAt(0).toUpperCase()}
              </Text>
            </View>
          )}
          <Text style={styles.dropdownArrow}>▼</Text>
        </TouchableOpacity>

        {showMenu && (
          <View style={styles.dropdownMenu}>
            {/* Profile Info */}
            <View style={styles.dropdownHeader}>
              <Text style={styles.dropdownName}>{displayName}</Text>
              <Text style={styles.dropdownEmail}>{auth.currentUser?.email}</Text>
            </View>

            {/* Organization Switcher */}
            {currentOrganization && (
              <>
                <View style={styles.menuDivider} />
                <View style={styles.dropdownOrgSection}>
                  <Text style={styles.dropdownOrgLabel}>Organization</Text>
                  <Text style={styles.dropdownOrgName}>{currentOrganization.name}</Text>
                  {organizations && organizations.length > 1 && (
                    <View style={styles.orgListCompact}>
                      {organizations.filter(o => o.id !== currentOrganization.id).slice(0, 2).map((org) => (
                        <TouchableOpacity
                          key={org.id}
                          style={styles.orgSwitchItem}
                          onPress={() => switchOrganization(org.id)}
                        >
                          <Text style={styles.orgSwitchText}>Switch to: {org.name}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                  <TouchableOpacity
                    style={[styles.manageOrgButtonDesktop, { backgroundColor: colors.accent }]}
                    onPress={() => handleNavigate('organization')}
                  >
                    <Text style={styles.manageOrgTextDesktop}>Manage Organization</Text>
                  </TouchableOpacity>
                  {pendingInvitations && pendingInvitations.length > 0 && (
                    <Text style={[styles.inviteNotice, { color: colors.accent }]}>
                      {pendingInvitations.length} pending invite(s)
                    </Text>
                  )}
                </View>
              </>
            )}

            <View style={styles.menuDivider} />

            {/* Theme Toggle */}
            <TouchableOpacity 
              style={[styles.dropdownMenuItem, styles.themeToggleItem]}
              onPress={toggleTheme}
              activeOpacity={0.7}
              className="dropdown-menu-item"
            >
              <Text style={styles.dropdownMenuItemText}>
                {isDark ? '☀️ Light Mode' : '🌙 Dark Mode'}
              </Text>
            </TouchableOpacity>

            <View style={styles.menuDivider} />

            {/* Menu Items */}
            <TouchableOpacity 
              style={styles.dropdownMenuItem}
              onPress={() => handleNavigate('dashboard')}
              activeOpacity={0.7}
              className="dropdown-menu-item"
            >
              <Text style={styles.dropdownMenuItemText}>Dashboard</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.dropdownMenuItem}
              onPress={() => handleNavigate('profile')}
              activeOpacity={0.7}
              className="dropdown-menu-item"
            >
              <Text style={styles.dropdownMenuItemText}>My Profile</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.dropdownMenuItem}
              onPress={() => handleNavigate('wizard')}
              activeOpacity={0.7}
              className="dropdown-menu-item"
            >
              <Text style={styles.dropdownMenuItemText}>Generate Proposal</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.dropdownMenuItem}
              onPress={() => handleNavigate('contracts')}
              activeOpacity={0.7}
              className="dropdown-menu-item"
            >
              <Text style={styles.dropdownMenuItemText}>📝 Contract Generator</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.dropdownMenuItem}
              onPress={() => handleNavigate('tasks')}
              activeOpacity={0.7}
              className="dropdown-menu-item"
            >
              <Text style={styles.dropdownMenuItemText}>Task Manager</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.dropdownMenuItem}
              onPress={() => handleNavigate('casestudy')}
              activeOpacity={0.7}
              className="dropdown-menu-item"
            >
              <Text style={styles.dropdownMenuItemText}>Case Study Creator</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.dropdownMenuItem}
              onPress={() => handleNavigate('settings')}
              activeOpacity={0.7}
              className="dropdown-menu-item"
            >
              <Text style={styles.dropdownMenuItemText}>Templates & Examples</Text>
            </TouchableOpacity>

            {isAdmin && (
              <TouchableOpacity 
                style={styles.dropdownMenuItem}
                onPress={() => handleNavigate('admin')}
                activeOpacity={0.7}
                className="dropdown-menu-item"
              >
                <Text style={styles.dropdownMenuItemText}>Admin Dashboard</Text>
              </TouchableOpacity>
            )}

            <View style={styles.menuDivider} />

            <TouchableOpacity 
              style={styles.dropdownMenuItem}
              onPress={handleSignOut}
              activeOpacity={0.7}
              className="dropdown-menu-item"
            >
              <Text style={[styles.dropdownMenuItemText, styles.signOutText]}>Sign Out</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  // Mobile Hamburger
  hamburgerButton: {
    padding: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  hamburgerLine: {
    width: 24,
    height: 3,
    backgroundColor: '#fff',
    marginVertical: 2,
    borderRadius: 2,
  },

  // Mobile Overlay
  mobileOverlay: {
    flex: 1,
    flexDirection: 'row',
  },
  mobileOverlayBackground: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  mobileMenuPanel: {
    width: 280,
    backgroundColor: '#1a1a1a',
    borderRightWidth: 1,
    borderRightColor: '#333',
  },
  mobileMenuHeader: {
    padding: 20,
    paddingTop: 60,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
    alignItems: 'center',
  },
  profileImageContainer: {
    marginBottom: 15,
  },
  profileImage: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#2a2a2a',
  },
  profileImagePlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#4a9eff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileImagePlaceholderText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#fff',
  },
  mobileMenuName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 5,
  },
  mobileMenuEmail: {
    fontSize: 14,
    color: '#888',
  },
  mobileMenuItems: {
    paddingVertical: 10,
  },
  mobileMenuItem: {
    paddingVertical: 15,
    paddingHorizontal: 20,
  },
  mobileMenuItemText: {
    fontSize: 16,
    color: '#fff',
  },
  themeToggleMenuItem: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    marginHorizontal: 15,
    borderRadius: 8,
    marginTop: 10,
  },
  closeButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    padding: 10,
  },
  closeButtonText: {
    fontSize: 24,
    color: '#fff',
  },

  // Desktop Menu
  desktopMenuContainer: {
    position: 'relative',
    zIndex: 2000,
  },
  profileButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 5,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.05)',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  profileImageSmall: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#2a2a2a',
  },
  profileImagePlaceholderSmall: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#4a9eff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileImagePlaceholderTextSmall: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#fff',
  },
  dropdownArrow: {
    fontSize: 10,
    color: '#fff',
    marginLeft: 8,
    marginRight: 5,
  },
  desktopOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1998,
    backgroundColor: 'transparent',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    width: 240,
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#333',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
    elevation: 10000,
    zIndex: 1999,
    animation: 'dropdownSlideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    transformOrigin: 'top right',
  },
  dropdownHeader: {
    padding: 15,
    backgroundColor: '#1a1a1a',
  },
  dropdownName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 3,
  },
  dropdownEmail: {
    fontSize: 13,
    color: '#888',
  },
  dropdownMenuItem: {
    paddingVertical: 12,
    paddingHorizontal: 15,
    backgroundColor: '#1a1a1a',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  dropdownMenuItemText: {
    fontSize: 14,
    color: '#fff',
    userSelect: 'none',
  },
  themeToggleItem: {
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  menuDivider: {
    height: 1,
    backgroundColor: '#333',
    marginVertical: 5,
  },
  signOutText: {
    color: '#ff4444',
  },
  // Organization Switcher Styles
  orgSwitcher: {
    padding: 15,
    marginHorizontal: 15,
    marginTop: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  orgLabel: {
    fontSize: 12,
    marginBottom: 10,
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  orgSelector: {
    gap: 8,
    marginBottom: 12,
  },
  orgOption: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  orgName: {
    fontSize: 14,
    fontWeight: '500',
  },
  manageOrgButton: {
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 8,
  },
  manageOrgText: {
    fontSize: 14,
    fontWeight: '600',
  },
  inviteBadge: {
    padding: 8,
    borderRadius: 6,
    marginTop: 10,
    alignItems: 'center',
  },
  inviteBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  // Desktop Org Styles
  dropdownOrgSection: {
    padding: 12,
    paddingHorizontal: 15,
  },
  dropdownOrgLabel: {
    fontSize: 11,
    color: '#999',
    marginBottom: 6,
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  dropdownOrgName: {
    fontSize: 15,
    color: '#fff',
    fontWeight: '600',
    marginBottom: 8,
  },
  orgListCompact: {
    gap: 6,
    marginBottom: 8,
  },
  orgSwitchItem: {
    padding: 8,
    backgroundColor: '#252525',
    borderRadius: 6,
  },
  orgSwitchText: {
    fontSize: 13,
    color: '#aaa',
  },
  manageOrgButtonDesktop: {
    padding: 10,
    borderRadius: 6,
    alignItems: 'center',
    marginTop: 6,
  },
  manageOrgTextDesktop: {
    fontSize: 13,
    color: '#fff',
    fontWeight: '600',
  },
  inviteNotice: {
    fontSize: 12,
    marginTop: 8,
    fontWeight: '600',
  },
});

